import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import hrService from '../../../services/hrService';
import { formatEasternDateOnly } from '../../../utils/timezone';

const PendingApprovals = ({ onApprove, onReject }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load pending leave requests from database
  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const requests = await hrService?.getPendingLeaveRequests();
      setPendingRequests(requests || []);
    } catch (err) {
      console.error('Error loading pending requests:', err);
      setError(err?.message || 'Failed to load pending requests');
      setPendingRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return 'Palmtree';
      case 'sick': return 'Heart';
      case 'personal': return 'User';
      case 'emergency': return 'AlertTriangle';
      default: return 'Calendar';
    }
  };

  const getLeaveTypeLabel = (type) => {
    switch (type) {
      case 'vacation': return 'Vacation Leave';
      case 'sick': return 'Sick Leave';
      case 'personal': return 'Personal Leave';
      case 'emergency': return 'Emergency Leave';
      default: return type;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatEasternDateOnly(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleApproval = async (requestId, action, comment = '') => {
    setActionLoading(`${requestId}-${action}`);
    try {
      const approved = action === 'approve';
      
      // Call the parent component's approval handler if provided
      if (onApprove && approved) {
        await onApprove(requestId, approved, comment);
      } else if (onReject && !approved) {
        await onReject(requestId, approved, comment);
      } else {
        // Fallback to direct hrService call
        await hrService?.approveLeaveRequest(requestId, approved, comment);
      }
      
      // Remove from local state
      setPendingRequests(prev => prev?.filter(req => req?.id !== requestId));
      
      // Close modal if open
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error processing approval:', error);
      setError(`Failed to ${action} leave request`);
    } finally {
      setActionLoading(null);
    }
  };

  const ApprovalModal = ({ request, onClose, onApprove, onReject }) => {
    const [comment, setComment] = useState('');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1000 p-4">
        <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Review Leave Request</h3>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Employee Info */}
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {request?.employees?.userProfiles?.fullName || request?.employees?.fullName || 'Unknown Employee'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {request?.employees?.employeeId || 'N/A'} • {request?.employees?.department || 'Unknown'}
                </p>
              </div>
            </div>

            {/* Leave Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground">Leave Type</label>
                <p className="text-sm text-muted-foreground">{getLeaveTypeLabel(request?.leaveType)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Duration</label>
                <p className="text-sm text-muted-foreground">{request?.totalDays || 0} day{(request?.totalDays || 0) !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Start Date</label>
                <p className="text-sm text-muted-foreground">{formatDate(request?.startDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">End Date</label>
                <p className="text-sm text-muted-foreground">{formatDate(request?.endDate)}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Reason</label>
              <p className="text-sm text-muted-foreground mt-1">{request?.reason || 'No reason provided'}</p>
            </div>

            {/* Comment Section */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e?.target?.value)}
                placeholder="Add any comments or feedback..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="p-6 border-t border-border flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={actionLoading === `${request?.id}-reject`}
              onClick={() => onReject(request?.id, comment)}
              iconName="X"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Reject
            </Button>
            <Button
              variant="success"
              loading={actionLoading === `${request?.id}-approve`}
              onClick={() => onApprove(request?.id, comment)}
              iconName="Check"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Approve
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={20} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Pending Approvals</h2>
            <p className="text-sm text-muted-foreground">Review and approve team leave requests</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">Loading pending requests...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={20} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Pending Approvals</h2>
            <p className="text-sm text-muted-foreground">Review and approve team leave requests</p>
          </div>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h3 className="text-lg font-medium text-destructive mb-2">Error loading requests</h3>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <Button onClick={loadPendingRequests} variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
          <Icon name="Clock" size={20} color="var(--color-warning)" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Pending Approvals</h2>
          <p className="text-sm text-muted-foreground">Review and approve team leave requests</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {pendingRequests?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground">No pending leave requests to review.</p>
          </div>
        ) : (
          pendingRequests?.map((request) => (
            <div key={request?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start space-x-4 mb-4 lg:mb-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-foreground">
                        {request?.employees?.userProfiles?.fullName || request?.employees?.fullName || 'Unknown Employee'}
                      </h3>
                      <span className="text-xs text-muted-foreground">
                        {request?.employees?.employeeId || 'N/A'} • {request?.employees?.department || 'Unknown'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name={getLeaveTypeIcon(request?.leaveType)} size={16} color="var(--color-primary)" />
                        <span className="text-sm font-medium text-foreground">
                          {getLeaveTypeLabel(request?.leaveType)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Calendar" size={14} />
                        <span>
                          {formatDate(request?.startDate)} - {formatDate(request?.endDate)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Clock" size={14} />
                        <span>{request?.totalDays || 0} day{(request?.totalDays || 0) !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {request?.reason || 'No reason provided'}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Applied: {formatDate(request?.createdAt)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRequest(request)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    Review
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    loading={actionLoading === `${request?.id}-reject`}
                    onClick={() => handleApproval(request?.id, 'reject')}
                    iconName="X"
                    iconPosition="left"
                  >
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    loading={actionLoading === `${request?.id}-approve`}
                    onClick={() => handleApproval(request?.id, 'approve')}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedRequest && (
        <ApprovalModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onApprove={(id, comment) => handleApproval(id, 'approve', comment)}
          onReject={(id, comment) => handleApproval(id, 'reject', comment)}
        />
      )}
    </div>
  );
};

export default PendingApprovals;